import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"

export default class extends Controller {
  static targets = [ "bankSelect", "branchSelect" ]

  static values = {
    banksUrl: String,
    branchesUrlTemplate: String,
    selectedBankId: String,
    selectedBankLabel: String,
    selectedBranchId: String,
    selectedBranchLabel: String
  }

  connect() {
    this.setupBankSelect()
    this.setupBranchSelect()
    this.applyInitialValues()
  }

  disconnect() {
    if (this.bankTomSelect) {
      this.bankTomSelect.destroy()
      this.bankTomSelect = null
    }

    if (this.branchTomSelect) {
      this.branchTomSelect.destroy()
      this.branchTomSelect = null
    }
  }

  setupBankSelect() {
    if (!this.hasBankSelectTarget || !this.hasBanksUrlValue) return

    this.bankTomSelect = new TomSelect(this.bankSelectTarget, {
      valueField: "value",
      labelField: "text",
      searchField: [ "text" ],
      maxOptions: 20,
      maxItems: 1,
      create: false,
      plugins: [ "clear_button" ],
      preload: true,
      hideSelected: false,
      openOnFocus: true,
      placeholder: "銀行を検索",
      render: {
        no_results: (data, escape) => `<div class="px-4 py-2 text-sm text-slate-500">"${escape(data.input)}" に一致する銀行がありません</div>`
      },
      load: (query, callback) => {
        this.loadOptions({
          endpoint: this.banksUrlValue,
          query,
          selectedId: this.selectedBankIdValue,
          callback
        })
      },
      onChange: (value) => this.handleBankChange(value)
    })
  }

  setupBranchSelect() {
    if (!this.hasBranchSelectTarget || !this.hasBranchesUrlTemplateValue) return

    this.branchTomSelect = new TomSelect(this.branchSelectTarget, {
      valueField: "value",
      labelField: "text",
      searchField: [ "text" ],
      maxOptions: 20,
      maxItems: 1,
      create: false,
      plugins: [ "clear_button" ],
      preload: false,
      hideSelected: false,
      openOnFocus: true,
      placeholder: "支店を検索",
      render: {
        no_results: (data, escape) => `<div class="px-4 py-2 text-sm text-slate-500">"${escape(data.input)}" に一致する支店がありません</div>`
      },
      load: (query, callback) => {
        const bankId = this.bankTomSelect?.getValue()
        if (!bankId) {
          callback([])
          return
        }

        this.loadOptions({
          endpoint: this.branchesUrlTemplateValue.replace("__BANK_ID__", bankId),
          query,
          selectedId: this.selectedBranchIdValue,
          callback
        })
      }
    })

    this.setBranchDisabled(true)
  }

  applyInitialValues() {
    if (this.hasSelectedBankIdValue && this.selectedBankIdValue && this.bankTomSelect) {
      this.bankTomSelect.addOption({
        value: this.selectedBankIdValue,
        text: this.selectedBankLabelValue
      })
      this.bankTomSelect.setValue(this.selectedBankIdValue, true)
      this.setBranchDisabled(false)
    }

    if (this.hasSelectedBranchIdValue && this.selectedBranchIdValue && this.branchTomSelect) {
      this.branchTomSelect.addOption({
        value: this.selectedBranchIdValue,
        text: this.selectedBranchLabelValue
      })
      this.branchTomSelect.setValue(this.selectedBranchIdValue, true)
    }
  }

  handleBankChange(bankId) {
    if (!this.branchTomSelect) return

    this.selectedBankIdValue = bankId || ""
    this.selectedBranchIdValue = ""
    this.branchTomSelect.clear(true)
    this.branchTomSelect.clearOptions()
    this.setBranchDisabled(!bankId)
  }

  setBranchDisabled(disabled) {
    if (!this.branchTomSelect) return

    if (disabled) {
      this.branchTomSelect.disable()
    } else {
      this.branchTomSelect.enable()
    }
  }

  loadOptions({ endpoint, query, selectedId, callback }) {
    const url = new URL(endpoint, window.location.origin)
    if (query) url.searchParams.set("q", query)
    if (selectedId) url.searchParams.set("selected_id", selectedId)

    fetch(url.toString(), {
      credentials: "same-origin",
      headers: { Accept: "application/json" }
    })
      .then((response) => {
        if (!response.ok) throw new Error(`status=${response.status}`)
        return response.json()
      })
      .then((json) => callback(json))
      .catch((error) => {
        console.error("Failed to load bank options", error)
        callback([])
      })
  }
}
